import {
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigItemDefinition,
  type ToggleButtonStateKey,
} from '@blaze-shared/elements-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { DEFAULT_ICON_TO_TEXT_RATIO } from '@blaze-shared/theming-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { type ToggleButtonPropsModel } from './toggle-button.props-model';

export const TOGGLE_BUTTON_SIZE_LIMITS = {
  MIN_SIZE: 48,
  MAX_WIDTH: 900,
  MAX_HEIGHT: 150,
} as const;

export const TOGGLE_BUTTON_STATE_OPTIONS: SelectOption<ToggleButtonStateKey>[] = [
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

export const TOGGLE_BUTTON_DEFAULT_STATE_OPTIONS: SelectOption<ToggleButtonStateKey>[] = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'enabled', label: 'Enabled' },
];

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('toggleButton', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.toggle',
        'Toggle',
        [
          epc.fields.selectFieldFromList(
            'content.toggle.defaultState',
            'Default',
            TOGGLE_BUTTON_DEFAULT_STATE_OPTIONS,
            { showSelection: true, allowNone: false },
          ),
          epc.fields.radiosField('content.toggle.stateView', 'State', TOGGLE_BUTTON_STATE_OPTIONS, {
            hideLabel: true,
          }),
          ...stateContentFields('enabled'),
          ...stateContentFields('disabled'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Value', dataType: 'Boolean' }],
            },
          ],
        },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.subsection('content.toggle', 'Size', [
        epc.root.elementWidthField(
          {
            min: TOGGLE_BUTTON_SIZE_LIMITS.MIN_SIZE,
            max: TOGGLE_BUTTON_SIZE_LIMITS.MAX_WIDTH,
            unit: 'px',
            step: 1,
          },
          ['fixed', 'full'],
        ),
        epc.root.elementHeightField(
          {
            min: TOGGLE_BUTTON_SIZE_LIMITS.MIN_SIZE,
            max: TOGGLE_BUTTON_SIZE_LIMITS.MAX_HEIGHT,
            unit: 'px',
            step: 1,
          },
          ['min', 'fixed'],
        ),
      ]),

      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.button', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.button.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),

        epc.subsections.subsection('style.states', 'Label', [
          epc.fields.radiosField('style.labelStateView', 'State', TOGGLE_BUTTON_STATE_OPTIONS, {
            hideLabel: true,
          }),
          ...stateLabelStyleFields('enabled'),
          ...stateLabelStyleFields('disabled'),
        ]),

        epc.subsections.subsection('style.states', 'Background', [
          epc.fields.radiosField(
            'style.backgroundStateView',
            'State',
            TOGGLE_BUTTON_STATE_OPTIONS,
            {
              hideLabel: true,
            },
          ),
          ...stateBackgroundStyleFields('enabled'),
          ...stateBackgroundStyleFields('disabled'),
        ]),

        epc.subsections.labelStyleSubsection({
          color: 'style.general.title',
          enabled: 'content.title.enabled',
          fontFamily: 'style.labelBlock.fontFamily',
          fontSize: 'style.labelBlock.fontSize',
          fontStyle: 'style.labelBlock.fontStyle',
          fontWeight: 'style.labelBlock.fontWeight',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.labelBlock.iconSize',
          lineHeight: 'style.labelBlock.lineHeight',
          position: 'content.title.position',
          width: 'content.title.width',
        }),
      ],
      true,
    ),
  ]);
}

function stateContentFields(state: ToggleButtonStateKey): PropertiesConfigItemDefinition[] {
  const base = `content.toggle.${state}`;
  return epc.utils.conditionalFields(
    [
      epc.fields.textInput(`${base}.label`, 'Label'),
      epc.fields.iconField(`${base}.icon`),
      epc.fields.iconPositionField(`${base}.iconPosition`, `${base}.icon`),
    ],
    'content.toggle.stateView',
    (stateView: ToggleButtonStateKey | undefined) => (stateView ?? 'enabled') !== state,
  );
}

function stateLabelStyleFields(state: ToggleButtonStateKey): PropertiesConfigItemDefinition[] {
  const base = `style.states.${state}`;
  return epc.utils.conditionalFields(
    [
      epc.fields.colorField(`${base}.label.color`, 'Color', {
        allowGradient: false,
        allowNone: false,
      }),
      ...epc.fields.fontFamilyAndWeightFields(`${base}.font.fontFamily`, `${base}.font.fontWeight`),
      epc.fields.fontStyleField(`${base}.font.style`, 'Decoration', ['line-through', 'bold']),
      epc.utils.observedField(
        epc.fields.sliderField(`${base}.font.fontSize`, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
        (model: ToggleButtonPropsModel, value: ValueWithUnitOfMeasure) => {
          const fontSizeNumber = Number.parseInt(value);
          if (Number.isNaN(fontSizeNumber)) {
            // If number is unknown, return default icon size
            return { [`${base}.label.iconSize`]: '$t.buttonIconSize' };
          }

          // Calculate icon size based on font size
          const iconSizeString = `${Math.round(fontSizeNumber * DEFAULT_ICON_TO_TEXT_RATIO)}px`;
          return { [`${base}.label.iconSize`]: iconSizeString };
        },
      ),
      epc.fields.horizontalAlignField(`${base}.label.align`),
      epc.fields.sliderField(`${base}.label.iconSize`, 'Icon size', [
        { min: 8, max: 48, unit: 'px' },
      ]),
    ],
    'style.labelStateView',
    (stateView: ToggleButtonStateKey | undefined) => (stateView ?? 'enabled') !== state,
  );
}

function stateBackgroundStyleFields(state: ToggleButtonStateKey): PropertiesConfigItemDefinition[] {
  const base = `style.states.${state}.background`;
  return epc.utils.conditionalFields(
    [
      epc.fields.colorField(`${base}.fill`, 'Fill'),
      epc.utils.observedField(
        epc.fields.colorField(`${base}.outline`, 'Outline', { allowGradient: false }),
        (model: ToggleButtonPropsModel, value: PropertiesConfigInputColorModel) => {
          const outlineWidth: ValueWithUnitOfMeasure | undefined =
            model.style.states[state].background.outlineWidth;
          if (
            value.selectorType !== 'none' &&
            model.style.states[state].background.outline.selectorType === 'none' &&
            outlineWidth === '0px'
          ) {
            return { [`${base}.outlineWidth`]: '1px' };
          }
          return {};
        },
      ),
      epc.utils.conditionalField(
        epc.fields.sliderField(`${base}.outlineWidth`, 'Width', [{ min: 0, max: 10, unit: 'px' }]),
        `${base}.outline`,
        (value: PropertiesConfigInputColorModel) => value.selectorType === 'none',
      ),
      epc.fields.radiusField(`${base}.radius`),
    ],
    'style.backgroundStateView',
    (stateView: ToggleButtonStateKey | undefined) => (stateView ?? 'enabled') !== state,
  );
}
